import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CODE_LENGTH, CodeInput } from '@/components/ui/CodeInput';
import { AuthShell } from './LoginPage';
import { humanizeError } from '@/lib/errors';
import { studentLanding } from '@/lib/loginReturn';

/** Куда человек попадает после подтверждения — та же логика, что на входе (ученик — `studentLanding()`). */
function landingFor(role?: string): string {
  return role === 'admin' || role === 'manager' ? '/admin' : role === 'curator' ? '/curator' : studentLanding();
}

/**
 * Адрес показываем маской: экран может открыться на общем или чужом устройстве,
 * и полный адрес там подсказывать нечего. Первый символ и домен оставляем —
 * по ним человек узнаёт свой ящик и замечает опечатку.
 */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 1) return email;
  const name = email.slice(0, at);
  const domain = email.slice(at);
  if (name.length <= 2) return name[0] + '*' + domain;
  return name[0] + '*'.repeat(Math.min(name.length - 2, 6)) + name[name.length - 1] + domain;
}

/**
 * Код ошибки от auth-service → ключ перевода. Экспортируется ради проверки:
 * тест сверяет этот список с кодами, которые реально возвращает обработчик на Go,
 * — иначе новый код ошибки молча превратится в техническую английскую строку
 * на экране у ученика.
 */
export function codeErrorKey(message: string): string | null {
  switch (message) {
    case 'invalid_code':
      return 'verifyEmail.errInvalid';
    case 'code_expired':
      return 'verifyEmail.errExpired';
    case 'code_not_requested':
      return 'verifyEmail.errNotRequested';
    case 'too_many_attempts':
      return 'verifyEmail.errAttempts';
    case 'code_recently_sent':
      return 'verifyEmail.errCooldown';
    case 'too_many_codes':
      return 'verifyEmail.errTooMany';
    case 'email_taken':
      return 'verifyEmail.errTaken';
    case 'same_email':
      return 'verifyEmail.errSame';
    case 'email_already_verified':
      return 'verifyEmail.errAlready';
    default:
      return null;
  }
}

function messageOf(e: unknown): string {
  return (e as { message?: string })?.message ?? '';
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const [code, setCode] = useState('');
  // Смена ключа очищает клетки после неверного кода и после смены адреса.
  const [resetKey, setResetKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');


  const showError = useCallback(
    (e: unknown) => {
      const key = codeErrorKey(messageOf(e));
      setError(key ? t(key) : humanizeError(e));
    },
    [t],
  );

  const sendCode = useCallback(async () => {
    setError(null);
    setNotice(null);
    try {
      const { retry_after_seconds } = await authApi.requestEmailCode();
      setCooldown(retry_after_seconds || 60);
      setNotice(t('verifyEmail.resent'));
    } catch (e) {
      // Пауза между письмами — не ошибка человека: письмо уже в пути, просто
      // показываем отсчёт вместо красной строки.
      if (messageOf(e) === 'code_recently_sent') {
        setCooldown(60);
        setNotice(t('verifyEmail.alreadySent'));
        return;
      }
      showError(e);
    }
  }, [showError, t]);

  // При открытии экрана письмо НЕ отправляется. Регистрация уже отправила код, а
  // экран открывают и повторно — из полосы-напоминания, обновлением страницы,
  // возвратом назад. Отправка на каждом открытии сжигала бы часовой лимит
  // (5 писем) и присылала бы человеку письма, которых он не просил. Кнопка
  // «Отправить снова» доступна сразу — если письма нет, он нажмёт сам.

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const submit = useCallback(
    async (value: string) => {
      setSubmitting(true);
      setError(null);
      setNotice(null);
      try {
        await authApi.verifyEmail(value);
        // Обновляем пользователя в хранилище, иначе полоса-напоминание
        // останется висеть до следующего входа.
        if (user) setUser({ ...user, email_verified_at: new Date().toISOString() });
        navigate(landingFor(user?.role), { replace: true });
      } catch (e) {
        showError(e);
        setCode('');
        setResetKey((k) => k + 1);
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, setUser, showError, user],
  );

  const changeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await authApi.changeUnverifiedEmail(newEmail);
      if (user) setUser({ ...user, email: newEmail });
      setEditingEmail(false);
      setCode('');
      setResetKey((k) => k + 1);
      setCooldown(60);
      setNotice(t('verifyEmail.resent'));
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified_at) return <Navigate to={landingFor(user.role)} replace />;

  return (
    <AuthShell title={t('verifyEmail.title')} back={false}>
      <div className="space-y-5">
        <p className="text-sm text-on-surface-variant">
          {t('verifyEmail.sentTo', { email: maskEmail(user.email) })}
        </p>

        {editingEmail ? (
          <form onSubmit={changeEmail} className="space-y-3">
            <Input
              type="email"
              autoComplete="email"
              label={t('verifyEmail.newEmailLabel')}
              placeholder="you@gmail.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />
            {error && <p className="text-sm text-error">{error}</p>}
            <div className="flex gap-2">
              <Button type="submit" loading={submitting} fullWidth>
                {t('verifyEmail.changeSubmit')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditingEmail(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <CodeInput
              onComplete={(value) => void submit(value)}
              onChange={setCode}
              disabled={submitting}
              resetKey={resetKey}
            />

            {error && <p className="text-sm text-error">{error}</p>}
            {!error && notice && <p className="text-sm text-success">{notice}</p>}
            <p className="text-xs text-on-surface-variant">{t('verifyEmail.hint')}</p>

            <Button
              type="button"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={code.length < CODE_LENGTH}
              onClick={() => void submit(code)}
            >
              {t('verifyEmail.submit')}
            </Button>

            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <button
                type="button"
                onClick={() => void sendCode()}
                disabled={cooldown > 0}
                className="font-semibold text-primary disabled:text-on-surface-variant"
              >
                {cooldown > 0 ? t('verifyEmail.resendIn', { seconds: cooldown }) : t('verifyEmail.resend')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewEmail(user.email);
                  setEditingEmail(true);
                }}
                className="text-on-surface-variant hover:text-primary"
              >
                {t('verifyEmail.changeEmail')}
              </button>
            </div>

            {/* «Позже» — сознательно: запирать человека на этом экране значит
                терять того, у кого письмо задержалось. Напоминание остаётся
                полосой сверху. */}
            <button
              type="button"
              onClick={() => navigate(landingFor(user.role), { replace: true })}
              className="w-full text-center text-sm text-on-surface-variant hover:text-primary"
            >
              {t('verifyEmail.later')}
            </button>
          </>
        )}
      </div>
    </AuthShell>
  );
}
